import { useFrontendConfigContext } from '@apeiron/config'
import { PUBLIC_RENTAL_GROUP_ID } from '@apeiron/orbital'
import { PlanetLite } from '@apeiron/planet'
import { Star } from '@apeiron/star'
import { createStrictContext } from '@apeiron/temp'
import useGetOrbitalTrackDetail from '@src/hooks/graphql/useGetOrbitalTrackDetail'
import useGetStar from '@src/hooks/graphql/useGetStar'
import {
  OrbitalTrackDetail,
  OrbitalTrackSetting
} from '@src/types/orbitalTrack'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import {
  Dispatch,
  FC,
  ReactChild,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import useAsyncEffect from 'use-async-effect'

const [ContextProvider, useOrbitalTrackAttachContext] =
  createStrictContext<ContextType>('OrbitalTrackAttach')

export { useOrbitalTrackAttachContext }

export const OrbitalTrackAttachProvider: FC<Props> = (props: Props) => {
  const { children } = props

  const { config } = useFrontendConfigContext()

  const { orbitRental: rentalConfig } = config || {}

  const [starId, setStarId] = useState<string>('')

  const [groupId, setGroupId] = useState<string>('')

  const [done, setDone] = useState<boolean>(false)

  const [filledPlanets, setFilledPlanets] = useState<PlanetLite[]>([])

  const [selectedPlanets, setSelectedPlanets] = useState<
    Array<PlanetLite | null>
  >([])

  const [showAttachPlanet, setShowAttachPlanet] = useState<boolean>(false)

  const [showStarSelect, setShowStarSelect] = useState<boolean>(false)

  const [availableTracks, setAvailableTracks] = useState<number>(0)

  const [star, setStar] = useState<Star | null>(null)

  const [trackDetail, setTrackDetail] = useState<OrbitalTrackDetail | null>(
    null
  )

  const [trackCooldown, setTrackCooldown] = useState<number>(0)

  const { fetch: fetchTrackDetail, loading: isFetchingTrack } =
    useGetOrbitalTrackDetail()

  const {
    data: fetchedStar,
    refetch: refetchStar,
    loading: isFetchingStar
  } = useGetStar(starId)

  const loading = isFetchingStar || isFetchingTrack

  const setting = useMemo((): OrbitalTrackSetting | null => {
    if (R.isNil(trackDetail) || R.isEmpty(groupId)) {
      return null
    }

    if (groupId !== PUBLIC_RENTAL_GROUP_ID) {
      return (
        R.find(
          R.propEq(groupId, 'inviteGroupId'),
          trackDetail.inviteSettings
        ) || null
      )
    }

    return trackDetail.openSetting
  }, [groupId, trackDetail])

  const setAndFillSelectedPlanets = useCallback(
    async (planets: PlanetLite[]) => {
      if (!R.isNil(trackDetail)) {
        const total = R.propOr(0, 'totalTrack', setting) as number

        const occupied = R.propOr(0, 'occupiedTrack', setting) as number

        const ungrouped = R.propOr(
          0,
          'availableOrbitalTrackCount',
          trackDetail
        ) as number

        const maxAttachEachTime = rentalConfig?.maxAttachEachTime || 0

        const maxAttach = !R.isNil(setting)
          ? total - occupied // use on planet owner rent
          : ungrouped // use on star owner attach

        const count = Math.min(maxAttach, maxAttachEachTime) - R.length(planets)

        const nullList = R.repeat(null, count)

        setFilledPlanets(planets)

        setAvailableTracks(count)

        setSelectedPlanets([...planets, ...nullList])
      }
    },
    [rentalConfig, setting, trackDetail]
  )

  const resetSelect = useCallback(() => {
    setDone(false)
    setAndFillSelectedPlanets([])
  }, [setAndFillSelectedPlanets])

  const resetContext = useCallback(() => {
    resetSelect()
    setStarId('')
    setStar(null)
    setTrackDetail(null)
  }, [resetSelect])

  useEffect(() => {
    if (RA.isNotNilOrEmpty(starId)) {
      refetchStar()
    }
  }, [starId])

  useEffect(() => {
    if (!R.isNil(fetchedStar)) {
      setStar(fetchedStar)
    }
  }, [fetchedStar])

  useAsyncEffect(
    async (isActive: () => boolean) => {
      if (!R.isNil(star)) {
        const result = await fetchTrackDetail(star.id)

        if (!isActive()) {
          return
        }

        const detail = R.propOr(
          null,
          'data',
          result
        ) as OrbitalTrackDetail | null

        setTrackDetail(detail)
      }
    },
    [star]
  )

  useEffect(() => {
    if (!R.isNil(trackDetail)) {
      setAndFillSelectedPlanets([])
    }
  }, [trackDetail, setAndFillSelectedPlanets])

  useEffect(() => {
    setTrackCooldown(R.pathOr(0, ['orbitRental', 'cooldown'], config))
  }, [])

  return (
    <ContextProvider
      value={{
        availableTracks,
        done,
        filledPlanets,
        groupId,
        loading,
        selectedPlanets,
        setting,
        showAttachPlanet,
        showStarSelect,
        star,
        trackCooldown,
        trackDetail,
        refetchStar,
        resetContext,
        resetSelect,
        setAndFillSelectedPlanets,
        setDone,
        setGroupId,
        setShowAttachPlanet,
        setShowStarSelect,
        setStar,
        setStarId
      }}
    >
      {children}
    </ContextProvider>
  )
}

type ContextType = {
  availableTracks: number
  done: boolean
  filledPlanets: PlanetLite[]
  groupId: string
  loading: boolean
  selectedPlanets: Array<PlanetLite | null>
  setting: OrbitalTrackSetting | null
  showAttachPlanet: boolean
  showStarSelect: boolean
  star: Star | null
  trackCooldown: number
  trackDetail: OrbitalTrackDetail | null
  refetchStar: () => void
  resetContext: () => void
  resetSelect: () => void
  setAndFillSelectedPlanets: (planets: PlanetLite[]) => void
  setDone: Dispatch<SetStateAction<boolean>>
  setGroupId: Dispatch<SetStateAction<string>>
  setShowAttachPlanet: Dispatch<SetStateAction<boolean>>
  setShowStarSelect: Dispatch<SetStateAction<boolean>>
  setStar: Dispatch<SetStateAction<Star | null>>
  setStarId: Dispatch<SetStateAction<string>>
}

export type Props = {
  children: ReactChild | ReactChild[]
}
